import { InjectionToken, NgModule, Provider } from '@angular/core';
import {
  AuthApi,
  BookingApi,
  CarApi,
  CashDrawerApi,
  ContactInformationApi,
  DemoApi,
  FeatureApi,
  FlightApi,
  OrderApi,
  PartnerApi,
  PaxApi,
  PaymentApi,
  PrepaidTicketApi,
  PriceListApi,
  ReportApi,
  ResourceApi,
  RoleApi,
  SettingApi,
  UserApi,
} from '@lkp/parking-sdk';
import { Configuration } from '@lkp/parking-sdk';
import { Observable, catchError, throwError } from 'rxjs';
import { SessionService } from '../services/session.service';
import { environment } from 'src/environments/environment';

const TOKEN_KEY = 'auth-token';
const ORDER_TOKEN_KEY = 'order-auth-token';

export const AUTH_API = new InjectionToken<AuthApi>('AuthApi');
export const BOOKING_API = new InjectionToken<BookingApi>('BookingApi');
export const CAR_API = new InjectionToken<CarApi>('CarApi');
export const CASH_DRAWER_API = new InjectionToken<CashDrawerApi>(
  'CashDrawerApi'
);
export const CONTACT_INFORMATION_API =
  new InjectionToken<ContactInformationApi>('ContactInformationApi');
export const DEMO_API = new InjectionToken<DemoApi>('DemoApi');
export const FEATURE_API = new InjectionToken<FeatureApi>('FeatureApi');
export const FLIGHT_API = new InjectionToken<FlightApi>('FlightApi');
export const ORDER_API = new InjectionToken<OrderApi>('OrderApi');
export const PARTNER_API = new InjectionToken<PartnerApi>('PartnerApi');
export const PAX_API = new InjectionToken<PaxApi>('PaxApi');
export const PAYMENT_API = new InjectionToken<PaymentApi>('PaymentApi');
export const PREPAID_TICKET_API = new InjectionToken<PrepaidTicketApi>(
  'PrepaidTicketApi'
);
export const PRICE_LIST_API = new InjectionToken<PriceListApi>('PriceListApi');
export const REPORT_API = new InjectionToken<ReportApi>('ReportApi');
export const RESOURCE_API = new InjectionToken<ResourceApi>('ResourceApi');
export const ROLE_API = new InjectionToken<RoleApi>('RoleApi');
export const SETTING_API = new InjectionToken<SettingApi>('SettingApi');
export const USER_API = new InjectionToken<UserApi>('UserApi');
export const API_CONFIGURATION = new InjectionToken<Configuration>(
  'Configuration'
);

type RequestCapableApi = {
  request: (...args: any[]) => Observable<unknown>;
};

function isUnauthorizedRequest(error: unknown): boolean {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? Number((error as { status?: unknown }).status)
      : undefined;

  return status === 401;
}

function withUnauthorizedSessionHandling<T>(
  api: T,
  sessionService: SessionService
): T {
  // Wrap the SDK instance to inject the order token for same-origin '/private' calls.
  // The SDK uses rxjs/ajax (not Angular HttpClient), so Angular interceptors won't run.
  const requestCapableApi = api as any;
  // Keep a reference to the original request so we can call it after tweaking headers.
  const originalRequest = (requestCapableApi.request as any).bind(requestCapableApi) as (
    ...args: any[]
  ) => any;

  // Override the low-level request function in a type-relaxed way to avoid generic conflicts.
  (requestCapableApi as any).request = (...args: any[]): any => {
    try {
      const ctx = args[0];
      const baseUrl = environment.backendServerUrl;
      let absoluteUrl: string | undefined;
      // Normalize to an absolute URL for consistent, safe origin/path checks.
      if (typeof ctx === 'string') {
        const raw = ctx as string;
        absoluteUrl = raw.startsWith('http')
          ? raw
          : `${baseUrl}${raw.startsWith('/') ? '' : '/'}${raw}`;
      } else if (ctx && typeof ctx === 'object') {
        // Some SDKs pass a request context object; derive the absolute URL from its parts.
        const urlPart: string | undefined = (ctx as any).url || (ctx as any).path;
        const basePart: string | undefined = (ctx as any).basePath;
        if (urlPart && urlPart.startsWith('http')) {
          absoluteUrl = urlPart;
        } else if (basePart && basePart.startsWith('http')) {
          absoluteUrl = `${basePart}${urlPart ? (urlPart.startsWith('/') ? '' : '/') + urlPart : ''}`;
        } else if (urlPart) {
          absoluteUrl = `${baseUrl}${urlPart.startsWith('/') ? '' : '/'}${urlPart}`;
        } else if (basePart) {
          absoluteUrl = basePart;
        }
      }

      if (absoluteUrl) {
        // Security gate: only swap tokens for requests targeting our backend origin AND '/private' paths.
        const isSameOrigin = absoluteUrl.startsWith(baseUrl);
        const isPrivatePath = absoluteUrl.includes('/private');

        if (isSameOrigin && isPrivatePath) {
        // Use the per-order token instead of the normal user auth token for private endpoints.
        const orderToken = localStorage.getItem(ORDER_TOKEN_KEY);
        if (orderToken && ctx) {
          // Attempt to override Authorization header in various runtime shapes
          if (ctx.headers) {
            // If headers has set method
            if (typeof ctx.headers.set === 'function') {
              ctx.headers.set('Authorization', `Bearer ${orderToken}`);
            } else if (typeof ctx.setHeaderParam === 'function') {
              ctx.setHeaderParam('Authorization', `Bearer ${orderToken}`);
            } else if (typeof ctx.headers === 'object') {
              ctx.headers = { ...ctx.headers, Authorization: `Bearer ${orderToken}` };
            }
          } else if (typeof ctx.setHeaderParam === 'function') {
            ctx.setHeaderParam('Authorization', `Bearer ${orderToken}`);
          }
        }
      }
      }
    } catch {
      // Best-effort header override; ignore errors and proceed to request
    }

    return (originalRequest(...args).pipe(
      // Preserve original observable behavior; also trigger logout flow on 401s.
      catchError((error) => {
        if (isUnauthorizedRequest(error)) {
          sessionService.handleUnauthorizedSession();
        }

        return throwError(() => error);
      })
    ) as any);
  };

  return api;
}

function provideSdkApi<T>(
  token: InjectionToken<T>,
  ApiClass: new (config: Configuration) => T
): Provider {
  return {
    provide: token,
    useFactory: (config: Configuration, sessionService: SessionService) =>
      withUnauthorizedSessionHandling(new ApiClass(config), sessionService),
    deps: [API_CONFIGURATION, SessionService],
  };
}

export function provideApiClient(): Provider[] {
  return [
    {
      provide: API_CONFIGURATION,
      useFactory: () => {
        return new Configuration({
          basePath: environment.backendServerUrl,
          accessToken: () => localStorage.getItem(TOKEN_KEY) ?? '',
        });
      },
    },
    provideSdkApi(AUTH_API, AuthApi),
    provideSdkApi(BOOKING_API, BookingApi),
    provideSdkApi(CAR_API, CarApi),
    provideSdkApi(CASH_DRAWER_API, CashDrawerApi),
    provideSdkApi(CONTACT_INFORMATION_API, ContactInformationApi),
    provideSdkApi(DEMO_API, DemoApi),
    provideSdkApi(FEATURE_API, FeatureApi),
    provideSdkApi(FLIGHT_API, FlightApi),
    provideSdkApi(ORDER_API, OrderApi),
    provideSdkApi(PARTNER_API, PartnerApi),
    provideSdkApi(PAX_API, PaxApi),
    provideSdkApi(PAYMENT_API, PaymentApi),
    provideSdkApi(PREPAID_TICKET_API, PrepaidTicketApi),
    provideSdkApi(PRICE_LIST_API, PriceListApi),
    provideSdkApi(REPORT_API, ReportApi),
    provideSdkApi(RESOURCE_API, ResourceApi),
    provideSdkApi(ROLE_API, RoleApi),
    provideSdkApi(SETTING_API, SettingApi),
    provideSdkApi(USER_API, UserApi),
  ];
}

@NgModule({
  providers: [...provideApiClient()],
})
export class ApiModule {}
